/**
 * Auth slice — the user's identity, roles, and auth status for the UI.
 *
 * NOTE: the raw access token is NOT stored here (Redux state is easy to dump).
 * It lives in tokenStore.js (in-memory). This slice only holds non-secret
 * identity/roles used for rendering and RBAC checks.
 */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  status: 'idle', // 'idle' | 'authenticating' | 'authenticated' | 'anonymous'
  user: null, // { email, firstName, lastName, org }
  roles: [], // mapped app roles, e.g. ['user', 'admin']
  /*
   * The CURRENT variant's branch of the server's FeaturesData — the per-feature
   * permission matrix behind role_name/role_id.
   *
   * Variant-aware on purpose. Legacy keeps the whole { CCA, EIA } object and
   * then hardcodes `.EIA` when reading it (store/index.js:32), so in CCA mode
   * its permission state holds EIA's rules. Only the active product's branch is
   * ever useful, so only that is kept.
   */
  features: null,
  /*
   * Why the session ended, when the user did not end it — 'Session expired.
   * Please login again.' (legacy apiservice.js:201). The session layer runs
   * outside React and cannot raise a toast itself, so it leaves the text here
   * and SessionNotice (mounted beside the toasts) shows it once.
   */
  notice: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authenticating(state) {
      state.status = 'authenticating';
    },
    authenticated(state, action) {
      state.status = 'authenticated';
      state.user = action.payload.user;
      state.roles = action.payload.roles ?? [];
      state.features = action.payload.features ?? null;
      state.notice = null;
    },
    /** `payload.notice` (optional): the message to show — an expired session. */
    signedOut(state, action) {
      state.status = 'anonymous';
      state.user = null;
      state.roles = [];
      state.features = null;
      state.notice = action?.payload?.notice ?? null;
    },
    noticeShown(state) {
      state.notice = null;
    },
  },
});

export const { authenticating, authenticated, signedOut, noticeShown } = authSlice.actions;

export const selectAuthStatus = (s) => s.auth.status;
export const selectUser = (s) => s.auth.user;
export const selectRoles = (s) => s.auth.roles;
export const selectFeatures = (s) => s.auth.features;
export const selectIsAuthenticated = (s) => s.auth.status === 'authenticated';
export const selectAuthNotice = (s) => s.auth.notice;

export default authSlice.reducer;
